<template>
  <sections-extra-layout>
    <template slot="title">{{ $t('wheel.gifts.title') }}</template>
    <template slot="description">{{ $t('wheel.gifts.description') }}</template>
    <template slot="content">
      <div class="flex">
        <div class="w-full md:w-1/4 mr-2">
          <span class="font-bold">{{ $t('wheel.gifts.enable.title') }}</span>
        </div>
        <div
          @click="checkToggle"
          class="mb-4 w-14 h-7 cursor-pointer flex items-center rounded-full mx-2 px-1"
          :class="{ 'bg-gray-300': validateToggle, 'bg-green-700': !validateToggle }"
        >
          <div
            class="bg-white w-5 h-5 rounded-full shadow-md"
            :class="{ 'transform-none': validateToggle, 'transform translate-x-7 mr-5': !validateToggle }"
          ></div>
        </div>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="28"
          height="28"
          viewBox="0 0 28 28"
          fill="none"
          class="ml-2 cursor-pointer"
          @click="getInfoGift"
        >
          <path
            d="M12.6 7H15.4V9.8H12.6V7ZM12.6 12.6H15.4V21H12.6V12.6ZM14 0C6.272 0 0 6.272 0 14C0 21.728 6.272 28 14 28C21.728 28 28 21.728 28 14C28 6.272 21.728 0 14 0ZM14 25.2C7.826 25.2 2.8 20.174 2.8 14C2.8 7.826 7.826 2.8 14 2.8C20.174 2.8 25.2 7.826 25.2 14C25.2 20.174 20.174 25.2 14 25.2Z"
            fill="#9CA3AF"
          />
        </svg>
      </div>

      <div class="flex">
        <div class="md:mr-2 md:w-1/4">&nbsp;</div>
        <div class="md:w-3/4">
          <p class="italic text-gray-400 mb-2">{{ $t('wheel.gifts.enable.toggle') }}</p>
        </div>
      </div>

      <wheels-ui-modal-gift-info v-if="modals.info" :open="modals.info" @close="modals.info = false" />

      <div v-if="localGiftEnable === 1" class="flex">
        <div class="w-full md:w-1/4 mr-2">
          <span class="font-bold">{{ $t('wheel.gifts.list') }}</span>
        </div>

        <div class="flex-1">
          <div v-for="(gift, index) in giftList" :key="index" class=" mb-4">
            <div class="my-2">Gift {{ index + 1 }}:</div>
            <div class="inline-block flex justify-between border rounded">
              <div class="px-5 py-5 bg-white text-sm">
                <p class="text-gray-900 whitespace-no-wrap mt-2">{{ gift.name }}</p>
              </div>

              <div class="px-5 py-5 bg-white text-sm text-right float-right">
                <span class="text-gray-500 relative inline-block py-1 leading-tight mt-2">
                  stock:
                </span>
                <span class="text-gray-900 relative inline-block px-1 py-1 leading-tight mr-2 mt-2">
                  {{ gift.availableCount }} {{ $t('wheel.gifts.out') }} {{ gift.stockCount }}
                </span>
              </div>

              <div class="px-5 py-5 bg-white text-sm text-right float-right">
                <span
                  class="relative inline-block px-3 py-1 leading-tight mr-2 mt-2"
                  v-for="lang in languages"
                  :key="lang"
                >
                  <span
                    class="absolute inset-0 rounded"
                    :class="{
                      'bg-blue-400': Object.keys(gift.label).includes(lang),
                      'bg-gray-200': !Object.keys(gift.label).includes(lang) && defaultLang !== lang,
                      'bg-red-500': !Object.keys(gift.label).includes(lang) && defaultLang === lang
                    }"
                  ></span>
                  <span class="relative text-white">{{ lang }}</span>
                </span>
              </div>
              <div class="px-5 py-5 bg-white text-sm text-right float-right">
                <button class="text-gray-600 bg-gray-200 rounded p-2" @click="confirmEdit(gift)">
                  <font-awesome-icon icon="pencil-alt"></font-awesome-icon>
                </button>
                <button class="text-gray-600 bg-gray-200 rounded p-2" @click="confirmDeletion(gift)">
                  <font-awesome-icon icon="trash"></font-awesome-icon>
                </button>
              </div>
            </div>

            <wheels-ui-modal-gift-create
              v-if="modals.edit"
              :wheelId="wheelId"
              :open="modals.edit"
              :gift="giftToEdit"
              @created-gift="$emit('edited-gift')"
              @close="stopEdit()"
            />

            <modal-confirmation
              v-if="deleteGiftModalConfirmation"
              :open="deleteGiftModalConfirmation"
              type="danger"
              messageTitle="Warnings !"
              message="Do you really want to remove this gift ? This actions cannot be undone."
              messageAction="Yes, I'm sure !"
              @execute="deleteGift(giftToDelete)"
              @close="deleteGiftModalConfirmation = false"
            />
          </div>

          <wheels-ui-modal-gift-create
            v-if="modals.create"
            :wheelId="wheelId"
            :open="modals.create"
            @created-gift="$emit('edited-gift')"
            @close="modals.create = false"
          />

          <div class="w-full md:w-3/4 flex">
            <button
              @click="modals.create = true"
              class="flex mt-2 px-4 py-2 rounded border border-gray-200 shadow-sm hover:shadow-md whitespace-nowrap text-gray-500"
            >
              <p class="pt-2 pr-3">{{ $t('wheel.gifts.new') }}</p>
              <div class="rounded px-3 py-1 bg-green-500 text-blue-50 font-bold text-lg">
                +
              </div>
            </button>
          </div>
        </div>
      </div>
    </template>
  </sections-extra-layout>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import ModalConfirmation from '@/components/others/utils/ModalConfirmation.vue'

export default {
  name: 'Gifts',
  components: { ModalConfirmation },
  props: {
    giftEnable: {
      type: Number,
      required: true
    },
    name: {
      type: String,
      required: true
    },
    wheelId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      localGiftEnable: this.giftEnable,
      giftList: [],
      fields: {
        status: 0
      },
      modals: {
        create: false,
        info: false,
        edit: false
      },
      lock: false,
      type: 'all',
      giftToDelete: null,
      giftToEdit: null,
      deleteGiftModalConfirmation: null
    }
  },
  computed: {
    ...mapGetters('account', {
      site: 'currentSite',
      defaultLang: 'defaultLang'
    }),
    ...mapGetters('gifts', {
      allGifts: 'gifts'
    }),
    validateToggle() {
      return this.localGiftEnable !== 1
    },
    languages() {
      return this.site.languages.map(lang => lang.lang)
    }
  },
  methods: {
    ...mapActions('gifts', ['loadGifts', 'removeGift']),
    confirmDeletion(gift) {
      this.giftToDelete = gift
      this.deleteGiftModalConfirmation = true
    },
    deleteGift(gift) {
      this.removeGift({ wheelId: this.wheelId, gift: gift })
      this.giftToDelete = null
      this.deleteGiftModalConfirmation = false
      this.init()
    },
    confirmEdit(gift) {
      this.giftToEdit = gift
      this.modals.edit = true
    },
    stopEdit() {
      this.giftToEdit = null
      this.modals.edit = false
    },
    init() {
      this.loadGifts(this.wheelId).then(() => {
        this.giftList = this.allGifts
      })
    },
    checkToggle() {
      if (this.localGiftEnable === 1) {
        this.localGiftEnable = 0
        this.$emit('update-giftEnable', 0)
      } else {
        this.getInfoGift()
        this.localGiftEnable = 1
        this.$emit('update-giftEnable', 1)
      }
    },
    changePrice(price) {
      price.selected = !price.selected
    },
    getInfoGift() {
      this.modals.info = true
    }
  },
  mounted() {
    this.init()
  }
}
</script>



// WEBPACK FOOTER //
// src/components/wheels/ui/form/gifts.vue